import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { TabProps } from './types';

// Topics and labels that are auto-managed and cannot be removed by the user
const AUTO_MANAGED_TOPICS = new Set(['special-approval']);
const AUTO_MANAGED_LABEL_KEYS = new Set(['special-approval', 'expires']);

export function LabelsTopicsTab({ config, onChange }: TabProps) {
  const [newTopic, setNewTopic] = useState('');
  const [newLabelKey, setNewLabelKey] = useState('');
  const [newLabelValue, setNewLabelValue] = useState('');

  const expiresTopic = config.project_end_date;

  const handleAddTopic = () => {
    const topic = newTopic.trim();
    if (!topic || config.topics.includes(topic)) return;
    onChange({ ...config, topics: [...config.topics, topic] });
    setNewTopic('');
  };

  const handleDeleteTopic = (topic: string) => {
    onChange({ ...config, topics: config.topics.filter(t => t !== topic) });
  };

  const handleAddLabel = () => {
    const key = newLabelKey.trim();
    const value = newLabelValue.trim();
    if (!key || !value) return;
    if (config.labels.some(l => l.key === key)) return;
    onChange({ ...config, labels: [...config.labels, { key, value }] });
    setNewLabelKey('');
    setNewLabelValue('');
  };

  const handleDeleteLabel = (key: string) => {
    onChange({ ...config, labels: config.labels.filter(l => l.key !== key) });
  };

  // Separate auto-managed topics from user-editable ones
  const autoTopics = config.topics.filter(t => AUTO_MANAGED_TOPICS.has(t));
  const userTopics = config.topics.filter(t => !AUTO_MANAGED_TOPICS.has(t));

  // Separate auto-managed labels from user-editable ones
  const autoLabels = config.labels.filter(l => AUTO_MANAGED_LABEL_KEYS.has(l.key));
  const userLabels = config.labels.filter(l => !AUTO_MANAGED_LABEL_KEYS.has(l.key));

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 24 }}>
      {/* Topics Section */}
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Topics
        </Typography>
        <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 12 }}>
          {/* Auto-generated expires topic (always shown) */}
          <Chip
            label={expiresTopic}
            disabled
            variant="outlined"
          />
          {/* Auto-managed topics (e.g. special-approval) */}
          {autoTopics.map(topic => (
            <Chip
              key={topic}
              label={topic}
              disabled
              variant="outlined"
              style={{ borderColor: '#d32f2f', color: '#d32f2f' }}
            />
          ))}
          {/* User-editable topics */}
          {userTopics.map(topic => (
            <Chip
              key={topic}
              label={topic}
              onDelete={() => handleDeleteTopic(topic)}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <TextField
            label="New topic"
            variant="outlined"
            size="small"
            value={newTopic}
            onChange={e => setNewTopic(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTopic();
              }
            }}
          />
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddTopic}
            disabled={!newTopic.trim()}
          >
            Add
          </Button>
        </Box>
      </Box>

      {/* Labels Section */}
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Labels
        </Typography>
        <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 12 }}>
          {/* Auto-generated expires label (always shown) */}
          <Chip
            label={`expires=${config.project_end_date}`}
            disabled
            variant="outlined"
          />
          {/* Auto-managed labels (e.g. special-approval) */}
          {autoLabels
            .filter(l => l.key !== 'expires')
            .map(label => (
              <Chip
                key={label.key}
                label={`${label.key}=${label.value}`}
                disabled
                variant="outlined"
                style={{ borderColor: '#d32f2f', color: '#d32f2f' }}
              />
            ))}
          {/* User-editable labels */}
          {userLabels.map(label => (
            <Chip
              key={label.key}
              label={`${label.key}=${label.value}`}
              onDelete={() => handleDeleteLabel(label.key)}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <TextField
            label="Key"
            variant="outlined"
            size="small"
            value={newLabelKey}
            onChange={e => setNewLabelKey(e.target.value)}
          />
          <TextField
            label="Value"
            variant="outlined"
            size="small"
            value={newLabelValue}
            onChange={e => setNewLabelValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddLabel();
              }
            }}
          />
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddLabel}
            disabled={!newLabelKey.trim() || !newLabelValue.trim()}
          >
            Add
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
